import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const nodeExpansionSetContext =
  createContext<NodeExpansionSetContextType | null>(null);

export type NodeExpansionSetContextType = {
  expansionSet: Set<number>;
  expand: (v: number) => void;
  collapse: (v: number) => void;
};

export function NodeExpansionSetContextProvider({
  children,
}: React.PropsWithChildren<unknown>) {
  const [expansionSet, setExpansionSet] = useState(new Set<number>());

  const expand = useCallback((v: number) => {
    setExpansionSet((c) => {
      c.add(v);
      return new Set(c);
    });
  }, []);

  const collapse = useCallback((v: number) => {
    setExpansionSet((c) => {
      c.delete(v);
      return new Set(c);
    });
  }, []);

  const value = useMemo(
    () => ({
      expansionSet,
      expand,
      collapse,
    }),
    [expansionSet, expand, collapse],
  );

  return (
    <nodeExpansionSetContext.Provider value={value}>
      {children}
    </nodeExpansionSetContext.Provider>
  );
}

export function useExpansionSet() {
  const exp = useContext(nodeExpansionSetContext);

  if (!exp) {
    throw new Error(`Cannot useExpansionSet from outside a provider`);
  }

  return exp;
}
